import {
  ExcalidrawLinearElement,
  ExcalidrawBindableElement,
  NonDeleted,
  NonDeletedExcalidrawElement,
  PointBinding,
  ExcalidrawElement,
} from "./types";
import { getElementAtPosition } from "../scene";
import { AppState } from "../types";
import {
  isBindableElement,
  isBindingElement,
  isLinearElement,
} from "./typeChecks";
import {
  bindingBorderTest,
  distanceToBindableElement,
  maxBindingGap,
  determineFocusDistance,
  intersectElementWithLine,
  determineFocusPoint,
} from "./collision";
import { mutateElement } from "./mutateElement";
import Scene from "../scene/Scene";
import { LinearElementEditor } from "./linearElementEditor";
import { arrayToMap, tupleToCoors } from "../utils";
import { KEYS } from "../keys";
import { getBoundTextElement, handleBindTextResize } from "./textElement";
import { cloneDeep } from "lodash";
import { ConnectionDirection } from "./transformHandles";
import { rotate } from "../math";
import { ROUNDNESS } from "../constants";

export type SuggestedBinding =
  | NonDeleted<ExcalidrawBindableElement>
  | SuggestedPointBinding;

export type SuggestedPointBinding = [
  NonDeleted<ExcalidrawLinearElement>,
  "start" | "end" | "both",
  NonDeleted<ExcalidrawBindableElement>,
];

export const shouldEnableBindingForPointerEvent = (
  event: React.PointerEvent<HTMLElement>,
) => {
  return !event[KEYS.CTRL_OR_CMD];
};

export const isBindingEnabled = (appState: AppState): boolean => {
  return appState.isBindingEnabled;
};

const getNonDeletedElements = (
  scene: Scene,
  ids: readonly ExcalidrawElement["id"][],
): NonDeleted<ExcalidrawElement>[] => {
  const result: NonDeleted<ExcalidrawElement>[] = [];
  ids.forEach((id) => {
    const element = scene.getNonDeletedElement(id);
    if (element != null) {
      result.push(element);
    }
  });
  return result;
};

export const bindOrUnbindLinearElement = (
  linearElement: NonDeleted<ExcalidrawLinearElement>,
  startBindingElement: ExcalidrawBindableElement | null | "keep",
  endBindingElement: ExcalidrawBindableElement | null | "keep",
  fixedConnectionDirection:any = null,
): void => {
  const boundToElementIds: Set<ExcalidrawBindableElement["id"]> = new Set();
  const unboundFromElementIds: Set<ExcalidrawBindableElement["id"]> = new Set();
  bindOrUnbindLinearElementEdge(
    linearElement,
    startBindingElement,
    endBindingElement,
    "start",
    boundToElementIds,
    unboundFromElementIds,
      fixedConnectionDirection,
  );
  bindOrUnbindLinearElementEdge(
    linearElement,
    endBindingElement,
    startBindingElement,
    "end",
    boundToElementIds,
    unboundFromElementIds,
      fixedConnectionDirection,
  );

  const onlyUnbound = Array.from(unboundFromElementIds).filter(
    (id) => !boundToElementIds.has(id),
  );

  getNonDeletedElements(Scene.getScene(linearElement)!, onlyUnbound).forEach(
    (element) => {
      mutateElement(element, {
        boundElements: element.boundElements?.filter(
          (element) =>
            element.type !== "arrow" || element.id !== linearElement.id,
        ),
      });
    },
  );
};

const bindOrUnbindLinearElementEdge = (
  linearElement: NonDeleted<ExcalidrawLinearElement>,
  bindableElement: ExcalidrawBindableElement | null | "keep",
  otherEdgeBindableElement: ExcalidrawBindableElement | null | "keep",
  startOrEnd: "start" | "end",
  // Is mutated
  boundToElementIds: Set<ExcalidrawBindableElement["id"]>,
  // Is mutated
  unboundFromElementIds: Set<ExcalidrawBindableElement["id"]>,
  fixedConnectionDirection:any = null,
): void => {
  if (bindableElement !== "keep") {
    if (bindableElement != null) {
      // Don't bind if we're trying to bind or are already bound to the same
      // element on the other edge already ("start" edge takes precedence).
      if (
        otherEdgeBindableElement == null ||
        (otherEdgeBindableElement === "keep"
          ? !isLinearElementSimpleAndAlreadyBoundOnOppositeEdge(
              linearElement,
              bindableElement,
              startOrEnd,
            )
          : startOrEnd === "start" ||
            otherEdgeBindableElement.id !== bindableElement.id)
      ) {
        bindLinearElement(linearElement, bindableElement, startOrEnd,fixedConnectionDirection);
        boundToElementIds.add(bindableElement.id);
      }
    } else {
      const unbound = unbindLinearElement(linearElement, startOrEnd);
      if (unbound != null) {
        unboundFromElementIds.add(unbound);
      }
    }
  }
};

export const bindOrUnbindSelectedElements = (
  elements: NonDeleted<ExcalidrawElement>[],
): void => {
  elements.forEach((element) => {
    if (isBindingElement(element)) {
      bindOrUnbindLinearElement(
        element,
        getElligibleElementForBindingElement(element, "start"),
        getElligibleElementForBindingElement(element, "end"),
      );
    } else if (isBindableElement(element)) {
      maybeBindBindableElement(element);
    }
  });
};

const maybeBindBindableElement = (
  bindableElement: NonDeleted<ExcalidrawBindableElement>,
): void => {
  getElligibleElementsForBindableElementAndWhere(bindableElement).forEach(
    ([linearElement, where]) =>
      bindOrUnbindLinearElement(
        linearElement,
        where === "end" ? "keep" : bindableElement,
        where === "start" ? "keep" : bindableElement,
      ),
  );
};

export const maybeBindLinearElement = (
  linearElement: NonDeleted<ExcalidrawLinearElement>,
  appState: AppState,
  scene: Scene,
  pointerCoords: { x: number; y: number },
): void => {
  if (appState.startBoundElement != null) {
    bindLinearElement(linearElement, appState.startBoundElement, "start");
  }
  const hoveredElement = getHoveredElementForBinding(pointerCoords, scene);
  if (
    hoveredElement != null &&
    !isLinearElementSimpleAndAlreadyBoundOnOppositeEdge(
      linearElement,
      hoveredElement,
      "end",
    )
  ) {
    bindLinearElement(linearElement, hoveredElement, "end");
  }
};

export const bindLinearElement = (
  linearElement: NonDeleted<ExcalidrawLinearElement>,
  hoveredElement: ExcalidrawBindableElement,
  startOrEnd: "start" | "end",
  fixedConnectionDirection:any = null,
): void => {
  const connectionStateKey = startOrEnd === "start" ? "startConnectionState" : "endConnectionState";
  const connectionDirectionKey = startOrEnd === "start" ? "startConnectionDirection" : "endConnectionDirection";

  // Check if this endpoint is already locked (has a connectionState)
  const existingState = hoveredElement[connectionStateKey]?.[linearElement.id];
  const existingDirection = hoveredElement[connectionDirectionKey]?.[linearElement.id] as ConnectionDirection;

  let focusPointAbs: [number, number];
  let connectionDirection: any;

  // If the endpoint is locked and has a direction, preserve it
  if (existingState && existingDirection) {
    // Use existing direction, calculate the focus point for that direction inline
    const { x, y, width, height, angle } = hoveredElement;
    const centerX = x + width / 2;
    const centerY = y + height / 2;
    let target: [number, number] = [centerX, centerY];

    switch (existingDirection) {
      case "top":
        target = [centerX, y];
        break;
      case "right":
        target = [x + width, centerY];
        break;
      case "bottom":
        target = [centerX, y + height];
        break;
      case "left":
        target = [x, centerY];
        break;
    }

    const rotated = rotate(target[0], target[1], centerX, centerY, angle);
    focusPointAbs = [rotated[0], rotated[1]];
    connectionDirection = {
      ...(hoveredElement[connectionDirectionKey] || {}),
      [linearElement.id]: existingDirection,
    };
  } else {
    // Not locked, calculate normally
    const result = getFocusPointForConnection(hoveredElement, linearElement, startOrEnd);
    focusPointAbs = result.focusPointAbs;
    connectionDirection = result.connectionDirection;

    // Clear the "fixed" state for new bindings
    const newConnectionState = { ...(hoveredElement[connectionStateKey] || {}) };
    delete newConnectionState[linearElement.id];

    mutateElement(hoveredElement, {
      [connectionStateKey]: newConnectionState,
    });
  }

  mutateElement(hoveredElement, {
    [connectionDirectionKey]: connectionDirection,
  });
  const points = cloneDeep(linearElement.points) as [number, number][];
  if (startOrEnd === "end") {
    points[points.length - 1] = [
      focusPointAbs[0] - linearElement.x,
      focusPointAbs[1] - linearElement.y,
    ];

    mutateElement(linearElement, {
      endBinding: {
        elementId: hoveredElement.id,
        ...calculateFocusAndGap(linearElement, hoveredElement, startOrEnd),
      } as PointBinding,
      points,
    });
  } else {
    mutateElement(linearElement, {
      startBinding: {
        elementId: hoveredElement.id,
        ...calculateFocusAndGap(linearElement, hoveredElement, startOrEnd),
      } as PointBinding,
      x: focusPointAbs[0],
      y: focusPointAbs[1],
      // points,
    });
  }

  const boundElementsMap = arrayToMap(hoveredElement.boundElements || []);
  if (!boundElementsMap.has(linearElement.id)) {
    mutateElement(hoveredElement, {
      boundElements: (hoveredElement.boundElements || []).concat({
        id: linearElement.id,
        type: "arrow",
      }),
    });
  }
};

// Don't bind both ends of a simple segment
const isLinearElementSimpleAndAlreadyBoundOnOppositeEdge = (
  linearElement: NonDeleted<ExcalidrawLinearElement>,
  bindableElement: ExcalidrawBindableElement,
  startOrEnd: "start" | "end",
): boolean => {
  const otherBinding =
    linearElement[startOrEnd === "start" ? "endBinding" : "startBinding"];
  return isLinearElementSimpleAndAlreadyBound(
    linearElement,
    otherBinding?.elementId,
    bindableElement,
  );
};

export const isLinearElementSimpleAndAlreadyBound = (
  linearElement: NonDeleted<ExcalidrawLinearElement>,
  alreadyBoundToId: ExcalidrawBindableElement["id"] | undefined,
  bindableElement: ExcalidrawBindableElement,
): boolean => {
  return (
    alreadyBoundToId === bindableElement.id && linearElement.points.length < 3
  );
};

export const unbindLinearElements = (
  elements: NonDeleted<ExcalidrawElement>[],
): void => {
  elements.forEach((element) => {
    if (isBindingElement(element)) {
      bindOrUnbindLinearElement(element, null, null);
    }
  });
};

const unbindLinearElement = (
  linearElement: NonDeleted<ExcalidrawLinearElement>,
  startOrEnd: "start" | "end",
): ExcalidrawBindableElement["id"] | null => {
  const field = startOrEnd === "start" ? "startBinding" : "endBinding";
  const binding = linearElement[field];
  if (binding == null) {
    return null;
  }

  const bindingElement = Scene.getScene(linearElement)?.getElement(
      binding.elementId,
  ) as ExcalidrawBindableElement | null;

  if (bindingElement) {
    const connDirection =
        bindingElement[
            startOrEnd === "start"
                ? "startConnectionDirection"
                : "endConnectionDirection"
            ] || {};

    delete connDirection[linearElement.id];

    // Also clean up the connection state (fixed/auto)
    const connState =
        bindingElement[
            startOrEnd === "start"
                ? "startConnectionState"
                : "endConnectionState"
            ] || {};

    delete connState[linearElement.id];

    const updatedConnectionDirection = {
      [startOrEnd === "start"
          ? "startConnectionDirection"
          : "endConnectionDirection"]: connDirection,
      [startOrEnd === "start"
          ? "startConnectionState"
          : "endConnectionState"]: connState,
    };
    mutateElement(bindingElement, updatedConnectionDirection);
  }

  mutateElement(linearElement, { [field]: null });
  return binding.elementId;
};

export const getHoveredElementForBinding = (
  pointerCoords: {
    x: number;
    y: number;
  },
  scene: Scene,
): NonDeleted<ExcalidrawBindableElement> | null => {
  const hoveredElement = getElementAtPosition(
    scene.getNonDeletedElements(),
    (element) =>
      isBindableElement(element, false) &&
      bindingBorderTest(element, pointerCoords),
  );
  return hoveredElement as NonDeleted<ExcalidrawBindableElement> | null;
};

const calculateFocusAndGap = (
  linearElement: NonDeleted<ExcalidrawLinearElement>,
  hoveredElement: ExcalidrawBindableElement,
  startOrEnd: "start" | "end",
): { focus: number; gap: number } => {
  const direction = startOrEnd === "start" ? -1 : 1;
  const edgePointIndex = direction === -1 ? 0 : linearElement.points.length - 1;
  const adjacentPointIndex = edgePointIndex - direction;
  const edgePoint = LinearElementEditor.getPointAtIndexGlobalCoordinates(
    linearElement,
    edgePointIndex,
  );
  const adjacentPoint = LinearElementEditor.getPointAtIndexGlobalCoordinates(
    linearElement,
    adjacentPointIndex,
  );
  return {
    focus: determineFocusDistance(hoveredElement, adjacentPoint, edgePoint),
    gap: Math.max(1, distanceToBindableElement(hoveredElement, edgePoint)),
  };
};

// Supports translating, rotating and scaling `changedElement` with bound
// linear elements.
// Because scaling involves moving the focus points as well, it is
// done before the `changedElement` is updated, and the `newSize` is passed
// in explicitly.
export const updateBoundElements = (
  changedElement: NonDeletedExcalidrawElement,
  options?: {
    simultaneouslyUpdated?: readonly ExcalidrawElement[];
    newSize?: { width: number; height: number };
    /** true only when user is actively dragging elements on canvas */
    isDragging?: boolean;
  },
) => {
  const boundLinearElements = (changedElement.boundElements ?? []).filter(
    (el) => el.type === "arrow",
  );
  if (boundLinearElements.length === 0) {
    return;
  }
  const { newSize, simultaneouslyUpdated } = options ?? {};
  const allowAutoSwitch = !!options?.isDragging;
  const simultaneouslyUpdatedElementIds = getSimultaneouslyUpdatedElementIds(
    simultaneouslyUpdated,
  );

  getNonDeletedElements(
    Scene.getScene(changedElement)!,
    boundLinearElements.map((el) => el.id),
  ).forEach((element) => {
    if (!isLinearElement(element)) {
      return;
    }

    const bindableElement = changedElement as ExcalidrawBindableElement;
    // In case the boundElements are stale
    if (!doesNeedUpdate(element, bindableElement)) {
      return;
    }
    const startBinding = maybeCalculateNewGapWhenScaling(
      bindableElement,
      element.startBinding,
      newSize,
    );
    const endBinding = maybeCalculateNewGapWhenScaling(
      bindableElement,
      element.endBinding,
      newSize,
    );
    // `linearElement` is being moved/scaled already, just update the binding
    if (simultaneouslyUpdatedElementIds.has(element.id)) {
      mutateElement(element, { startBinding, endBinding });
      return;
    }
    // For ELBOW (orthogonal) connectors we must not recompute & potentially
    // overwrite the opposite endpoint's connection direction when only one
    // bound element is being transformed. This keeps the connection anchored
    // on the same side (e.g. right edge stays right) while moving the other
    // widget around.
    // 对于所有连接线（折线、直线、曲线），只更新被移动元素对应的端点，
    // 另一端的端点保持固定，不随着被链接元素的移动而改变位置
    const isConnector = element.type === "arrow" || element.type === "line";

    // Special-case: for straight/curve connectors bound at both ends, if user
    // has NOT manually adjusted either endpoint (no fixed connection state),
    // then moving either widget should allow BOTH endpoints to auto-switch to
    // the shortest-path side (midpoint) — matching legacy behavior.
    //
    // Once user manually adjusts an endpoint, we set *ConnectionState and we
    // keep the current behavior (only update the moved side; the other side
    // stays stable).
    const shouldAutoSwitchBothEnds =
        isConnector &&
        element.roundness?.type !== ROUNDNESS.ELBOW &&
        element.points.length === 2 &&
        element.startBinding &&
        element.endBinding;

    if (shouldAutoSwitchBothEnds) {
      const scene = Scene.getScene(element);
      const startBindingElement = scene?.getElement(
          element.startBinding.elementId,
      ) as ExcalidrawBindableElement | null;
      const endBindingElement = scene?.getElement(
          element.endBinding.elementId,
      ) as ExcalidrawBindableElement | null;

      const isStartLocked = !!startBindingElement?.startConnectionState?.[element.id];
      const isEndLocked = !!endBindingElement?.endConnectionState?.[element.id];

      if (!isStartLocked && !isEndLocked) {
        // Only allow auto-switching (shortest-path side selection) during
        // actual user drags. This prevents restore/content-resize from
        // overwriting saved connection directions on project reopen.
        updateBoundPoint(
            element,
            "start",
            startBinding,
            bindableElement,
            allowAutoSwitch,
        );
        updateBoundPoint(
            element,
            "end",
            endBinding,
            bindableElement,
            allowAutoSwitch,
        );
      } else {
        if (startBinding?.elementId === bindableElement.id) {
          updateBoundPoint(element, "start", startBinding, bindableElement, false);
        }
        if (endBinding?.elementId === bindableElement.id) {
          updateBoundPoint(element, "end", endBinding, bindableElement, false);
        }
      }
    } else {
      if (!isConnector || startBinding?.elementId === bindableElement.id) {
        updateBoundPoint(element, "start", startBinding, bindableElement, false);
      }
      if (!isConnector || endBinding?.elementId === bindableElement.id) {
        updateBoundPoint(element, "end", endBinding, bindableElement, false);
      }
    }
    const boundText = getBoundTextElement(element);
    if (boundText) {
      handleBindTextResize(element, false);
    }
  });
};

const doesNeedUpdate = (
  boundElement: NonDeleted<ExcalidrawLinearElement>,
  changedElement: ExcalidrawBindableElement,
) => {
  return (
    boundElement.startBinding?.elementId === changedElement.id ||
    boundElement.endBinding?.elementId === changedElement.id
  );
};

const getSimultaneouslyUpdatedElementIds = (
  simultaneouslyUpdated: readonly ExcalidrawElement[] | undefined,
): Set<ExcalidrawElement["id"]> => {
  return new Set((simultaneouslyUpdated || []).map((element) => element.id));
};


/**
 * Helper to get the edge midpoint for a specific side (same as package-martin).
 * Returns undefined if no direction is stored.
 */
const getFocusPointAbsoluteForSide = (
    bindingElement: ExcalidrawBindableElement,
    linearElementId: string,
    startOrEnd: "start" | "end",
): readonly [number, number] | undefined => {
  const connectionDirection =
      startOrEnd === "start"
          ? bindingElement.startConnectionDirection?.[linearElementId]
          : bindingElement.endConnectionDirection?.[linearElementId];

  const centerX = bindingElement.x + bindingElement.width / 2;
  const centerY = bindingElement.y + bindingElement.height / 2;
  if (!connectionDirection) {
    return undefined;
  }

  switch (connectionDirection) {
    case "top":
      return rotate(
          bindingElement.x + bindingElement.width / 2,
          bindingElement.y,
          centerX,
          centerY,
          bindingElement.angle,
      );
    case "right":
      return rotate(
          bindingElement.x + bindingElement.width,
          bindingElement.y + bindingElement.height / 2,
          centerX,
          centerY,
          bindingElement.angle,
      );
    case "bottom":
      return rotate(
          bindingElement.x + bindingElement.width / 2,
          bindingElement.y + bindingElement.height,
          centerX,
          centerY,
          bindingElement.angle,
      );
    case "left":
      return rotate(
          bindingElement.x,
          bindingElement.y + bindingElement.height / 2,
          centerX,
          centerY,
          bindingElement.angle,
      );
    default:
      return undefined;
  }
};

/**
 * 更新绑定的边界点。
 * 此函数用于当一个绑定的元素（例如，线段的一端）发生变化时，更新与之绑定的另一个元素（如矩形）的边界点，
 * 以保持它们之间的绑定关系。
 *
 * @param linearElement 非删除状态的Excalidraw线性元素，即线段或直线。
 * @param startOrEnd 指定更新线段的起始点或结束点，值为"start"或"end"。
 * @param binding 表示绑定关系的对象，可能为null或undefined，包含绑定的元素id和焦点位置等信息。
 * @param changedElement 发生变化的Excalidraw可绑定元素。
 * @returns void 无返回值。
 */
const updateBoundPoint = (
    linearElement: NonDeleted<ExcalidrawLinearElement>,
    startOrEnd: "start" | "end",
    binding: PointBinding | null | undefined,
    changedElement: ExcalidrawBindableElement,
    allowAutoSwitch: boolean = false,
): void => {
  if (
      !binding ||
      (binding.elementId !== changedElement.id && linearElement.points.length > 2)
  ) {
    return;
  }

  const bindingElement = Scene.getScene(linearElement)?.getElement(
      binding.elementId,
  ) as ExcalidrawBindableElement | null;
  if (!bindingElement) {
    return;
  }

  const direction = startOrEnd === "start" ? -1 : 1;
  const edgePointIndex = direction === -1 ? 0 : linearElement.points.length - 1;

  // Check if this endpoint is locked (has connectionState set)
  const connectionStateKey = startOrEnd === "start" ? "startConnectionState" : "endConnectionState";
  const connectionDirectionKey = startOrEnd === "start" ? "startConnectionDirection" : "endConnectionDirection";
  const isLocked = !!bindingElement[connectionStateKey]?.[linearElement.id];

  // For straight/curve connectors bound at both ends, moving the START bound
  // element should also be able to auto-switch the start side (shortest-path),
  // same as when moving the END bound element. We do this by recomputing the
  // start direction based on the current END point position.
  //
  // Note: once the user manually adjusts an endpoint, we set
  // `startConnectionState/endConnectionState` and we must not auto-switch.
  if (
      startOrEnd === "start" &&
      allowAutoSwitch &&
      !isLocked &&
      linearElement.roundness?.type !== ROUNDNESS.ELBOW &&
      linearElement.points.length === 2 &&
      linearElement.startBinding &&
      linearElement.endBinding
  ) {
    const scene = Scene.getScene(linearElement);
    const endBindingElement = scene?.getElement(
        linearElement.endBinding.elementId,
    ) as ExcalidrawElement | null;
    const isEndLocked = !!endBindingElement?.endConnectionState?.[linearElement.id];
    if (endBindingElement && !isEndLocked) {
      const endPointAbs = LinearElementEditor.getPointAtIndexGlobalCoordinates(
          linearElement,
          -1,
      );
      const hoveredDirection =
          bindingElement.startConnectionDirection?.[linearElement.id] || null;
      const { direction } = updateBindingStartDirection({
        startBindingElement: bindingElement,
        lineElement: linearElement,
        sceneCoords: { x: endPointAbs[0], y: endPointAbs[1] },
        suggestedBindings: [endBindingElement],
        hoveredDirection,
      });
      updateBindingEndDirection({
        startBindingElement: bindingElement,
        lineElement: linearElement,
        sceneCoords: { x: endPointAbs[0], y: endPointAbs[1] },
        endBindingElement,
        startDirection: direction,
      });
    }
  }

  // IMPORTANT: use the correct side's stored direction.
  // Using a combined start/end lookup can pick the opposite side's direction
  // when both ends are bound, which causes drift/detach during transforms.
  let focusPointAbsolute = getFocusPointAbsoluteForSide(
      bindingElement,
      linearElement.id,
      startOrEnd,
  );

  if (!focusPointAbsolute) {
    const { focusPointAbs, connectionDirection } = getFocusPointForConnection(
        bindingElement,
        linearElement,
        startOrEnd,
    );

    focusPointAbsolute = focusPointAbs;

    // Only update direction if the endpoint is NOT locked
    // If locked, we should preserve the existing direction (even if it's missing, we don't want to auto-change it)
    if (!isLocked) {
      const updatedConnectionDirection = {
        [connectionDirectionKey]: connectionDirection,
      };
      mutateElement(bindingElement, updatedConnectionDirection);
    }
  }

  const newEdgePoint = focusPointAbsolute;

  // Recalculate focus and gap based on the new edge point position
  const adjacentPointIndex = edgePointIndex - (direction === -1 ? -1 : 1);
  const edgePoint = LinearElementEditor.getPointAtIndexGlobalCoordinates(
      linearElement,
      edgePointIndex,
  );
  const adjacentPoint = LinearElementEditor.getPointAtIndexGlobalCoordinates(
      linearElement,
      adjacentPointIndex,
  );

  // Update binding with recalculated focus and gap
  const updatedBinding: PointBinding = {
    ...binding,
    focus: determineFocusDistance(bindingElement, adjacentPoint, newEdgePoint),
    gap: Math.max(1, distanceToBindableElement(bindingElement, newEdgePoint)),
  };

  LinearElementEditor.movePoints(
      linearElement,
      [
        {
          index: edgePointIndex,
          point: LinearElementEditor.pointFromAbsoluteCoords(
              linearElement,
              newEdgePoint,
          ),
          // Important: for straight/curve connectors, `movePoints()` contains an
          // "auto shortest-path" routine that should run for lines that were NOT
          // manually adjusted by the user. When we update bindings due to the
          // bound element moving (e.g. dragging B around A), this call is the only
          // place where we can opt-in to that auto behavior.
          //
          // Once the user manually drags an endpoint, we set `*ConnectionState`
          // and `movePoints()` will skip auto-updates, so the endpoint remains
          // stable even across canvas exit/restore.
          isDragging: allowAutoSwitch && !isLocked,
        },
      ],
      {
        [startOrEnd === "start" ? "startBinding" : "endBinding"]: updatedBinding
      },
  );
};

export const getFocusPointForConnection = (
    bindingElement: ExcalidrawBindableElement,
    linearElement: NonDeleted<ExcalidrawLinearElement>,
    startOrEnd: string,
) => {
  const { x, y, width, height } = bindingElement;
  const centerX = x + width / 2;
  const centerY = y + height / 2;

  const { x: lineX, y: lineY, points, id: lineId } = linearElement;
  const lastPoint = points[points.length - 1];
  const lastPointAbsX = lastPoint[0] + lineX;
  const lastPointAbsY = lastPoint[1] + lineY;

  const firstPoint = points[0];
  const firstPointAbsX = firstPoint[0] + lineX;
  const firstPointAbsY = firstPoint[1] + lineY;

  let pointAbsX = startOrEnd === "start" ? firstPointAbsX : lastPointAbsX;
  let pointAbsY = startOrEnd === "start" ? firstPointAbsY : lastPointAbsY;

  const [pointerX, pointerY] = rotate(
      pointAbsX,
      pointAbsY,
      centerX,
      centerY,
      -bindingElement.angle,
  );
  pointAbsX = pointerX;
  pointAbsY = pointerY;

  // 获取连接方向，默认为空对象
  const connDirection =
      bindingElement[
          startOrEnd === "start"
              ? "startConnectionDirection"
              : "endConnectionDirection"
          ] || {};

  // 初始化焦点坐标
  let focusPointAbs: [number, number] = [centerX, centerY];
  let direction: ConnectionDirection = "";

  // 根据线性元素的最后一个点和绑定元素的边界确定焦点坐标和方向
  if (pointAbsY <= y) {
    focusPointAbs = [centerX, y];
    direction = "top";
  } else if (pointAbsX >= x + width) {
    focusPointAbs = [x + width, centerY];
    direction = "right";
  } else if (pointAbsY >= y + height) {
    focusPointAbs = [centerX, y + height];
    direction = "bottom";
  } else if (pointAbsX <= x) {
    focusPointAbs = [x, centerY];
    direction = "left";
  }

  focusPointAbs = rotate(
      focusPointAbs[0],
      focusPointAbs[1],
      centerX,
      centerY,
      bindingElement.angle,
  );
  // 更新连接方向
  connDirection[lineId] = direction;

  return {
    focusPointAbs,
    connectionDirection: connDirection,
    currentDirection: direction,
  };
};

const getFocusPointAbsolute = (
    bindingElement: ExcalidrawBindableElement,
    linearElementId: string,
): readonly [number, number] | undefined => {
  const connectionDirection =
      bindingElement.startConnectionDirection?.[linearElementId] ||
      bindingElement.endConnectionDirection?.[linearElementId];

  const centerX = bindingElement.x + bindingElement.width / 2;
  const centerY = bindingElement.y + bindingElement.height / 2;

  if (!connectionDirection) {
    return undefined;
  }

  switch (connectionDirection) {
    case "top":
      return rotate(
          bindingElement.x + bindingElement.width / 2,
          bindingElement.y,
          centerX,
          centerY,
          bindingElement.angle,
      );

    case "right":
      return rotate(
          bindingElement.x + bindingElement.width,
          bindingElement.y + bindingElement.height / 2,
          centerX,
          centerY,
          bindingElement.angle,
      );

    case "bottom":
      return rotate(
          bindingElement.x + bindingElement.width / 2,
          bindingElement.y + bindingElement.height,
          centerX,
          centerY,
          bindingElement.angle,
      );

    case "left":
      return rotate(
          bindingElement.x,
          bindingElement.y + bindingElement.height / 2,
          centerX,
          centerY,
          bindingElement.angle,
      );

    default:
      return undefined;
  }
};

const maybeCalculateNewGapWhenScaling = (
  changedElement: ExcalidrawBindableElement,
  currentBinding: PointBinding | null | undefined,
  newSize: { width: number; height: number } | undefined,
): PointBinding | null | undefined => {
  if (currentBinding == null || newSize == null) {
    return currentBinding;
  }
  const { gap, focus, elementId } = currentBinding;
  const { width: newWidth, height: newHeight } = newSize;
  const { width, height } = changedElement;
  const newGap = Math.max(
    1,
    Math.min(
      maxBindingGap(changedElement, newWidth, newHeight),
      gap * (newWidth < newHeight ? newWidth / width : newHeight / height),
    ),
  );
  return { elementId, gap: newGap, focus };
};

// TODO: this is a bottleneck, optimise
export const getEligibleElementsForBinding = (
  elements: NonDeleted<ExcalidrawElement>[],
): SuggestedBinding[] => {
  const includedElementIds = new Set(elements.map(({ id }) => id));
  return elements.flatMap((element) =>
    isBindingElement(element, false)
      ? (getElligibleElementsForBindingElement(
          element as NonDeleted<ExcalidrawLinearElement>,
        ).filter(
          (element) => !includedElementIds.has(element.id),
        ) as SuggestedBinding[])
      : isBindableElement(element, false)
      ? getElligibleElementsForBindableElementAndWhere(element).filter(
          (binding) => !includedElementIds.has(binding[0].id),
        )
      : [],
  );
};

const getElligibleElementsForBindingElement = (
  linearElement: NonDeleted<ExcalidrawLinearElement>,
): NonDeleted<ExcalidrawBindableElement>[] => {
  return [
    getElligibleElementForBindingElement(linearElement, "start"),
    getElligibleElementForBindingElement(linearElement, "end"),
  ].filter(
    (element): element is NonDeleted<ExcalidrawBindableElement> =>
      element != null,
  );
};

const getElligibleElementForBindingElement = (
  linearElement: NonDeleted<ExcalidrawLinearElement>,
  startOrEnd: "start" | "end",
): NonDeleted<ExcalidrawBindableElement> | null => {
  return getHoveredElementForBinding(
    getLinearElementEdgeCoors(linearElement, startOrEnd),
    Scene.getScene(linearElement)!,
  );
};

const getLinearElementEdgeCoors = (
  linearElement: NonDeleted<ExcalidrawLinearElement>,
  startOrEnd: "start" | "end",
): { x: number; y: number } => {
  const index = startOrEnd === "start" ? 0 : -1;
  return tupleToCoors(
    LinearElementEditor.getPointAtIndexGlobalCoordinates(linearElement, index),
  );
};

const getElligibleElementsForBindableElementAndWhere = (
  bindableElement: NonDeleted<ExcalidrawBindableElement>,
): SuggestedPointBinding[] => {
  return Scene.getScene(bindableElement)!
    .getNonDeletedElements()
    .map((element) => {
      if (!isBindingElement(element, false)) {
        return null;
      }
      const canBindStart = isLinearElementEligibleForNewBindingByBindable(
        element,
        "start",
        bindableElement,
      );
      const canBindEnd = isLinearElementEligibleForNewBindingByBindable(
        element,
        "end",
        bindableElement,
      );
      if (!canBindStart && !canBindEnd) {
        return null;
      }
      return [
        element,
        canBindStart && canBindEnd ? "both" : canBindStart ? "start" : "end",
        bindableElement,
      ];
    })
    .filter((maybeElement) => maybeElement != null) as SuggestedPointBinding[];
};

const isLinearElementEligibleForNewBindingByBindable = (
  linearElement: NonDeleted<ExcalidrawLinearElement>,
  startOrEnd: "start" | "end",
  bindableElement: NonDeleted<ExcalidrawBindableElement>,
): boolean => {
  const existingBinding =
    linearElement[startOrEnd === "start" ? "startBinding" : "endBinding"];
  return (
    existingBinding == null &&
    !isLinearElementSimpleAndAlreadyBoundOnOppositeEdge(
      linearElement,
      bindableElement,
      startOrEnd,
    ) &&
    bindingBorderTest(
      bindableElement,
      getLinearElementEdgeCoors(linearElement, startOrEnd),
    )
  );
};

// We need to:
// 1: Update elements not selected to point to duplicated elements
// 2: Update duplicated elements to point to other duplicated elements
export const fixBindingsAfterDuplication = (
  sceneElements: readonly ExcalidrawElement[],
  oldElements: readonly ExcalidrawElement[],
  oldIdToDuplicatedId: Map<ExcalidrawElement["id"], ExcalidrawElement["id"]>,
  // There are three copying mechanisms: Copy-paste, duplication and alt-drag.
  // Only when alt-dragging the new "duplicates" act as the "old", while
  // the "old" elements act as the "new copy" - essentially working reverse
  // to the other two.
  duplicatesServeAsOld?: "duplicatesServeAsOld" | undefined,
): void => {
  // First collect all the binding/bindable elements, so we only update
  // each once, regardless of whether they were duplicated or not.
  const allBoundElementIds: Set<ExcalidrawElement["id"]> = new Set();
  const allBindableElementIds: Set<ExcalidrawElement["id"]> = new Set();
  const shouldReverseRoles = duplicatesServeAsOld === "duplicatesServeAsOld";
  oldElements.forEach((oldElement) => {
    const { boundElements } = oldElement;
    if (boundElements != null && boundElements.length > 0) {
      boundElements.forEach((boundElement) => {
        if (shouldReverseRoles && !oldIdToDuplicatedId.has(boundElement.id)) {
          allBoundElementIds.add(boundElement.id);
        }
      });
      allBindableElementIds.add(oldIdToDuplicatedId.get(oldElement.id)!);
    }
    // NOTE: in this codebase connectors may be represented as `line` elements
    // (not only `arrow`). Treat any linear element that carries bindings as a
    // binding element for the purpose of id rewiring after duplication.
    if (isBindingElement(oldElement)) {
      if (oldElement.startBinding != null) {
        const { elementId } = oldElement.startBinding;
        if (shouldReverseRoles && !oldIdToDuplicatedId.has(elementId)) {
          allBindableElementIds.add(elementId);
        }
      }
      if (oldElement.endBinding != null) {
        const { elementId } = oldElement.endBinding;
        if (shouldReverseRoles && !oldIdToDuplicatedId.has(elementId)) {
          allBindableElementIds.add(elementId);
        }
      }
      if (oldElement.startBinding != null || oldElement.endBinding != null) {
        allBoundElementIds.add(oldIdToDuplicatedId.get(oldElement.id)!);
      }
    }
  });

  // Update the linear elements
  (
    sceneElements.filter(({ id }) =>
      allBoundElementIds.has(id),
    ) as ExcalidrawLinearElement[]
  ).forEach((element) => {
    const { startBinding, endBinding } = element;
    mutateElement(element, {
      startBinding: newBindingAfterDuplication(
        startBinding,
        oldIdToDuplicatedId,
      ),
      endBinding: newBindingAfterDuplication(endBinding, oldIdToDuplicatedId),
    });
  });

  // Update the bindable shapes
  sceneElements
    .filter(({ id }) => allBindableElementIds.has(id))
    .forEach((bindableElement) => {
      const { boundElements } = bindableElement;
      if (boundElements != null && boundElements.length > 0) {
        // `oldIdToDuplicatedId` maps old->new for duplicate/copy flows, but some
        // helpers (e.g. bindTextToShapeAfterDuplication) may have already
        // rewritten certain `boundElements` to *new ids* before we get here.
        // Therefore we must support both:
        // - boundElements containing old ids (need remap via the map)
        // - boundElements containing new ids (keep if they belong to this duplication)
        const duplicatedIdSet = new Set(oldIdToDuplicatedId.values());

        mutateElement(bindableElement, {
          // IMPORTANT:
          // When duplicating a bindable element (e.g. rectangle), we must NOT keep
          // references to *non-duplicated* bound linear elements (arrows/lines),
          // otherwise deleting the duplicate would delete the original connectors
          // (see actionDeleteSelected which deletes `boundElements` recursively).
          //
          // Therefore: keep only those bound elements that were duplicated as part
          // of the same operation (i.e. have an id mapping), and remap to the
          // duplicated ids.
          boundElements: boundElements
              .map((boundElement) => {
                const remappedId = oldIdToDuplicatedId.get(boundElement.id);
                if (remappedId) {
                  return { id: remappedId, type: boundElement.type };
                }
                if (duplicatedIdSet.has(boundElement.id)) {
                  return boundElement;
                }
                return null;
              })
              .filter(
                  (
                      boundElement,
                  ): boundElement is NonNullable<typeof boundElement> =>
                      boundElement != null,
              ),
        });
      }
    });
};

const newBindingAfterDuplication = (
  binding: PointBinding | null,
  oldIdToDuplicatedId: Map<ExcalidrawElement["id"], ExcalidrawElement["id"]>,
): PointBinding | null => {
  if (binding == null) {
    return null;
  }
  const { elementId, focus, gap } = binding;
  return {
    focus,
    gap,
    elementId: oldIdToDuplicatedId.get(elementId) ?? elementId,
  };
};

export const fixBindingsAfterDeletion = (
  sceneElements: readonly ExcalidrawElement[],
  deletedElements: readonly ExcalidrawElement[],
): void => {
  const deletedElementIds = new Set(
    deletedElements.map((element) => element.id),
  );
  // non-deleted which bindings need to be updated
  const affectedElements: Set<ExcalidrawElement["id"]> = new Set();
  deletedElements.forEach((deletedElement) => {
    if (isBindableElement(deletedElement)) {
      deletedElement.boundElements?.forEach((element) => {
        if (!deletedElementIds.has(element.id)) {
          affectedElements.add(element.id);
        }
      });
    } else if (isBindingElement(deletedElement)) {
      if (deletedElement.startBinding) {
        affectedElements.add(deletedElement.startBinding.elementId);
      }
      if (deletedElement.endBinding) {
        affectedElements.add(deletedElement.endBinding.elementId);
      }
    }
  });
  sceneElements
    .filter(({ id }) => affectedElements.has(id))
    .forEach((element) => {
      if (isBindableElement(element)) {
        mutateElement(element, {
          boundElements: newBoundElementsAfterDeletion(
            element.boundElements,
            deletedElementIds,
          ),
        });
      } else if (isBindingElement(element)) {
        mutateElement(element, {
          isDeleted:true,//删除连接线的元素。。。
          startBinding: newBindingAfterDeletion(
            element.startBinding,
            deletedElementIds,
          ),
          endBinding: newBindingAfterDeletion(
            element.endBinding,
            deletedElementIds,
          ),
        });
      }
    });
};

const newBindingAfterDeletion = (
  binding: PointBinding | null,
  deletedElementIds: Set<ExcalidrawElement["id"]>,
): PointBinding | null => {
  if (binding == null || deletedElementIds.has(binding.elementId)) {
    return null;
  }
  return binding;
};


/**
 * 获取删除元素后需要更新的绑定信息，不直接修改元素
 * 返回一个 Map，key 是元素 ID，value 是需要更新的属性
 */
export const getBindingUpdatesAfterDeletion = (
    sceneElements: readonly ExcalidrawElement[],
    deletedElements: readonly ExcalidrawElement[],
): Map<string, Partial<ExcalidrawElement>> => {
  const updates = new Map<string, Partial<ExcalidrawElement>>();
  const deletedElementIds = new Set(
      deletedElements.map((element) => element.id),
  );
  // non-deleted which bindings need to be updated
  const affectedElements: Set<ExcalidrawElement["id"]> = new Set();
  deletedElements.forEach((deletedElement) => {
    if (isBindableElement(deletedElement)) {
      deletedElement.boundElements?.forEach((element) => {
        if (!deletedElementIds.has(element.id)) {
          affectedElements.add(element.id);
        }
      });
    } else if (isBindingElement(deletedElement)) {
      if (deletedElement.startBinding) {
        affectedElements.add(deletedElement.startBinding.elementId);
      }
      if (deletedElement.endBinding) {
        affectedElements.add(deletedElement.endBinding.elementId);
      }
    }
  });
  sceneElements
      .filter(({ id }) => affectedElements.has(id))
      .forEach((element) => {
        if (isBindableElement(element)) {
          updates.set(element.id, {
            boundElements: newBoundElementsAfterDeletion(
                element.boundElements,
                deletedElementIds,
            ),
          });
        } else if (isBindingElement(element)) {
          updates.set(element.id, {
            isDeleted: true,
            startBinding: newBindingAfterDeletion(
                element.startBinding,
                deletedElementIds,
            ),
            endBinding: newBindingAfterDeletion(
                element.endBinding,
                deletedElementIds,
            ),
          });
        }
      });
  return updates;
};


const newBoundElementsAfterDeletion = (
  boundElements: ExcalidrawElement["boundElements"],
  deletedElementIds: Set<ExcalidrawElement["id"]>,
) => {
  if (!boundElements) {
    return null;
  }
  return boundElements.filter((ele) => !deletedElementIds.has(ele.id));
};


function getBindingDirection(
    element: ExcalidrawElement,
    sceneCoords: {
      x: number;
      y: number;
    },
    suggestedBindings: ExcalidrawElement[],
    hoveredDirection?: "top" | "right" | "bottom" | "left" | null,
): ConnectionDirection {
  let direction: ConnectionDirection = "";
  const { x, y, width, height } = element;
  let { x: pointerX, y: pointerY } = sceneCoords;
  const isVertical = ["top", "bottom"].includes(hoveredDirection);

  if (!suggestedBindings.length) {
    if (!isVertical) {
      if (pointerX >= x + width) {
        direction = "right";
      } else if (pointerX <= x) {
        direction = "left";
      } else if (pointerY >= y + height) {
        direction = "bottom";
      } else if (pointerY <= y) {
        direction = "top";
      }
      return direction;
    }
    if (pointerY >= y + height) {
      direction = "bottom";
    } else if (pointerY <= y) {
      direction = "top";
    } else if (pointerX >= x + width) {
      direction = "right";
    } else if (pointerX <= x) {
      direction = "left";
    }
    return direction;
  }
  const {
    x: suggestedElementX,
    width: suggestedElementWidth,
  } = suggestedBindings[0];
  if (!isVertical) {
    if (pointerX >= x + width && suggestedElementX > x + width) {
      direction = "right";
    } else if (pointerX <= x && x > suggestedElementX + suggestedElementWidth) {
      direction = "left";
    } else if (pointerY >= y + height) {
      direction = "bottom";
    } else if (pointerY <= y) {
      direction = "top";
    }
    return direction;
  }
  if (pointerY >= y + height) {
    direction = "bottom";
  } else if (pointerY <= y) {
    direction = "top";
  } else if (pointerX >= x + width && suggestedElementX > x + width) {
    direction = "right";
  } else if (pointerX <= x && x > suggestedElementX + suggestedElementWidth) {
    direction = "left";
  }

  return direction;
};

export const updateBindingStartDirection = ({
                                              startBindingElement,
                                              lineElement,
                                              sceneCoords,
                                              suggestedBindings,
                                              hoveredDirection,
                                            }: {
  startBindingElement: ExcalidrawElement;
  lineElement: ExcalidrawElement;
  sceneCoords: {
    x: number;
    y: number;
  };
  suggestedBindings: ExcalidrawElement[];
  hoveredDirection?: "top" | "right" | "bottom" | "left" | null;
}): {
  direction: ConnectionDirection;
  startBindingElement?: ExcalidrawElement;
} => {
  if (startBindingElement.startConnectionState?.[lineElement.id]) {
    return {
      startBindingElement,
      direction:
          startBindingElement.startConnectionDirection?.[lineElement.id] || "",
    };
  }
  const direction = getBindingDirection(
      startBindingElement,
      sceneCoords,
      suggestedBindings,
      hoveredDirection
  );

  if (!direction) {
    return { direction: "" };
  }
  mutateElement(startBindingElement, {
    startConnectionDirection: {
      ...startBindingElement.startConnectionDirection,
      [lineElement.id]: direction,
    },
  });
  return { startBindingElement, direction };
};

export function updatedraggingElementPoint(
    startBindingElement: ExcalidrawElement,
    direction: string,
    draggingElement: ExcalidrawElement,
    points: readonly (readonly [number, number])[],
    pointSceneCoords: { x: number; y: number },
) {
  if (!direction) {
    return;
  }

  const { x, y, width, height,angle } = startBindingElement;
  const centerX = x + width / 2;
  const centerY = y + height / 2;
  let rotatedCoords = [0, 0];

  switch (direction) {
    case "right":
      rotatedCoords = rotate(x + width, centerY, centerX, centerY, angle);
      break;
    case "bottom":
      rotatedCoords = rotate(centerX, y + height, centerX, centerY, angle);
      break;
    case "left":
      rotatedCoords = rotate(x, centerY, centerX, centerY, angle);
      break;
    case "top":
      rotatedCoords = rotate(centerX, y, centerX, centerY, angle);
      break;
  }
  mutateElement(draggingElement, {
    x: rotatedCoords[0],
    y: rotatedCoords[1],
    // @ts-ignore
    points: [
      ...points.slice(0, -1),
      [
        pointSceneCoords.x - rotatedCoords[0],
        pointSceneCoords.y - rotatedCoords[1],
      ],
    ],
  });
}


const getEndBindingDirection = {
  left: "right",
  right: "left",
  top: "bottom",
  bottom: "top",
};
export const updateBindingEndDirection = ({
                                            startBindingElement,
                                            lineElement,
                                            sceneCoords,
                                            endBindingElement,
                                            startDirection,
                                          }: {
  startBindingElement: ExcalidrawElement;
  lineElement: ExcalidrawElement;
  sceneCoords: {
    x: number;
    y: number;
  };
  endBindingElement: ExcalidrawElement;
  startDirection?: ConnectionDirection;
}) => {
  if (!startDirection || !endBindingElement) {
    return;
  }

  // If user has manually adjusted the end point (any state value means locked), don't auto-update direction
  if (endBindingElement.endConnectionState?.[lineElement.id]) {
    return;
  }

  const endDirection = getEndBindingDirection[startDirection] as
      | "top"
      | "right"
      | "bottom"
      | "left";

  mutateElement(endBindingElement, {
    endConnectionDirection: {
      ...endBindingElement.endConnectionDirection,
      [lineElement.id]: endDirection,
    },
  });
};
